import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

import type { FindingCode } from "./Finding";
import type { MatchMetric } from "./MatchMetric";

// Общий совет «как исправить» по коду находки — рядом с советами по данным (`fixes`).
// Нет записи — у находки и так есть совет в пояснении (смерти в меньшинстве, ганки…).
export const FIX_TIPS: Partial<Record<FindingCode, MessageDescriptor>> = {
  item_bought_late: msg({
    id: "analysis.tip.item_bought_late",
    message:
      "Buy the core items in the order strong players do; leave situational items until the core is done.",
  }),
  core_item_missing: msg({
    id: "analysis.tip.core_item_missing",
    message: "Add it to your build: most strong players on this hero take it.",
  }),
  off_build_early: msg({
    id: "analysis.tip.off_build_early",
    message: "Finish the core first: early situational items delay your power spike.",
  }),
  item_sold_soon: msg({
    id: "analysis.tip.item_sold_soon",
    message: "Plan purchases ahead: selling returns only half the price.",
  }),
  no_anti_heal: msg({
    id: "analysis.tip.no_anti_heal",
    message: "Buy anti-heal early when the enemy team heals a lot.",
  }),
  defense_mismatch: msg({
    id: "analysis.tip.defense_mismatch",
    message: "Buy resistance against the damage you take most.",
  }),
  lane_result: msg({
    id: "analysis.tip.lane_result",
    message: "In lane, last hits come first; trade when the enemy reaches for a creep.",
  }),
  costly_death: msg({
    id: "analysis.tip.costly_death",
    message:
      "Late in the match play safer before objectives and pushes: a death costs over a minute.",
  }),
  enemy_escaped: msg({
    id: "analysis.tip.enemy_escaped",
    message:
      "Keep a slow, stun or burst for finishing; don't chase into the enemy team without it.",
  }),
};

// Совет для строки «Ещё исправить» у находок, где совет — в пояснении карточки: в строке
// пояснения нет, а без совета строка — только диагноз.
export const ROW_TIPS: Partial<Record<FindingCode, MessageDescriptor>> = {
  died_outnumbered: msg({
    id: "analysis.tip.row.died_outnumbered",
    message:
      "If enemies have two more heroes nearby for more than a couple of seconds, fall back to your team.",
  }),
  died_alone: msg({
    id: "analysis.tip.row.died_alone",
    message: "Stay closer to your team, or back off when no ally is near.",
  }),
  missed_teamfight: msg({
    id: "analysis.tip.row.missed_teamfight",
    message: "When the team is about to fight, join it — keep an eye on the minimap.",
  }),
  lane_ganked: msg({
    id: "analysis.tip.row.lane_ganked",
    message: "Watch the map: if an enemy disappears from their lane, back off.",
  }),
  killed_by_objective: msg({
    id: "analysis.tip.row.killed_by_objective",
    message: "Don't walk under an enemy tower without support.",
  }),
};

// Общий совет по метрике. `Record` по union из контракта: новая метрика не соберётся без совета.
export const METRIC_TIPS: Record<MatchMetric, MessageDescriptor> = {
  souls_per_min: msg({
    id: "analysis.tip.metric.souls_per_min",
    message: "Farm between fights: clear neutral camps and break urns while there is no fight.",
  }),
  neutral_damage_per_min: msg({
    id: "analysis.tip.metric.neutral_damage_per_min",
    message: "Clear neutral camps on your way and when your lane is pushed.",
  }),
  buffs_per_min: msg({
    id: "analysis.tip.metric.buffs",
    message: "Break crates and urns around the map: permanent buffs add up over the match.",
  }),
  first_buff_time: msg({
    id: "analysis.tip.metric.buffs",
    message: "Break crates and urns around the map: permanent buffs add up over the match.",
  }),
  cs_share: msg({
    id: "analysis.tip.metric.cs_share",
    message: "Last-hit troopers yourself: every missed creep is lost souls.",
  }),
  hero_damage_per_min: msg({
    id: "analysis.tip.metric.hero_damage_per_min",
    message: "Stay in fights at a safe distance and use abilities on cooldown.",
  }),
  accuracy: msg({
    id: "analysis.tip.metric.accuracy",
    message: "Shoot from your weapon's effective range; aim at the body when tracking is hard.",
  }),
  crit_rate: msg({
    id: "analysis.tip.metric.crit_rate",
    message: "Aim at the head: headshots deal much more damage.",
  }),
  objective_damage_per_min: msg({
    id: "analysis.tip.metric.objective_damage_per_min",
    message: "After a won fight or while enemies are dead, hit the nearest building.",
  }),
  kill_participation: msg({
    id: "analysis.tip.metric.kill_participation",
    message: "Join the team when a fight starts: watch the map.",
  }),
  parry_rate: msg({
    id: "analysis.tip.metric.parry_rate",
    message: "Parry only heavy melee hits you see coming: a missed parry leaves you open.",
  }),
  damage_taken_per_min: msg({
    id: "analysis.tip.metric.damage_taken_per_min",
    message: "Use cover and keep your distance; buy resistance against the damage you take.",
  }),
  time_dead_share: msg({
    id: "analysis.tip.metric.time_dead_share",
    message: "Late in the match each death costs over a minute: play safer when respawn is long.",
  }),
  unspent_souls: msg({
    id: "analysis.tip.metric.unspent_souls",
    message: "Visit the shop more often: souls in your pocket don't fight.",
  }),
  unspent_ap: msg({
    id: "analysis.tip.metric.unspent_ap",
    message: "Spend ability points right away: each one is power.",
  }),
};
